<template>
    <div class="col-md-12">
        <div class="h2">Invoices</div>
        <div class="flash-message">
            <FlashMessage :position="'top'"></FlashMessage>
        </div>
        <ul class="nav nav-pills mb-3" id="pills-tab" role="tablist">
            <li class="nav-item" role="presentation">
                <button
                    class="nav-link"
                    :class="{ active: activeTab === 'tab1' }"
                    @click="changeTab('tab1')"
                    role="tab"
                >
                    Overview
                </button>
            </li>
            <li class="nav-item" role="presentation">
                <button
                    class="nav-link"
                    :class="{ active: activeTab === 'tab2' }"
                    @click="changeTab('tab2')"
                    role="tab"
                >
                    Booking
                </button>
            </li>
            <li class="nav-item" role="presentation">
                <button
                    class="nav-link"
                    :class="{ active: activeTab === 'tab3' }"
                    @click="changeTab('tab3')"
                    role="tab"
                >
                    Created invoices
                </button>
            </li>
        </ul>

        <div class="row mb-3 align-items-end">
            <div class="col-md-3">
                <label><b>Start</b></label>
                <div class="d-flex align-items-center bg-white border rounded-lg div-focus border-2">
                    <date-picker v-model="filters.date_single_from" required
                                 valueType="YYYY-MM-DD"
                                 input-class="border-0 py-0 shadow-none form-control bg-transparent"
                                 placeholder="dd/mm/yyyy"
                                 :lang="datePickerOptions.lang"
                                 format="DD/MM/YYYY"
                                 style="width: 100%"></date-picker>
                </div>
            </div>
            <div class="col-md-1 text-center pb-2">
                <span class="h1 m-0">~</span>
            </div>
            <div class="col-md-3">
                <label><b>End</b></label>
                <div class="d-flex align-items-center bg-white border rounded-lg div-focus border-2">
                    <date-picker v-model="filters.date_single_to" valueType="YYYY-MM-DD"
                                 input-class="border-0 py-0 shadow-none form-control bg-transparent"
                                 placeholder="dd/mm/yyyy"
                                 :lang="datePickerOptions.lang"
                                 format="DD/MM/YYYY"
                                 style="width: 100%"></date-picker>
                </div>
            </div>
            <div class="col-md-3" v-if="['tab2', 'tab3'].includes(activeTab)">
                <label><b>Client</b></label>
                <v-select
                    :options="clientList"
                    v-model="filters.client"
                    :clearable="false"
                    label="name"
                    id="client-name"
                    class="bg-white"
                >
                </v-select>
            </div>
            <div :class="activeTab === 'tab2' ? 'col-md-2' : 'col-md-5'" class="text-right pb-2">
                <button v-if="activeTab === 'tab2'" class="btn btn-primary" @click="createInvoices()">Book invoice</button>
            </div>
        </div>
        <div v-if="error" class="text-danger text-right">{{ error }}</div>

        <div class="tab-content mt-3" id="pills-tabContent">
            <div
                class="tab-pane fade"
                :class="{ 'show active': activeTab === 'tab1' }"
                id="tab1"
                role="tabpanel"
            >
                <table class="table table-bordered table-striped">
                    <thead>
                    <tr>
                        <th @click="sortClient('client_name')">Client</th>
                        <th @click="sortClient('amount')">Amount</th>
                        <th @click="sortClient('total_booking_sum')">Value</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="client in clientsWithoutInvoices">
                        <td>
                            <div @click="setClientData(client.client_id, client.client_name)">
                                <a href="#" @click="preventDefault"> <b>{{ client.client_name }}</b></a>
                            </div>
                        </td>
                        <td>{{ client.amount }}</td>
                        <td>{{ (client.total_booking_sum - parseFloat(client.deadline_fee_sum) + parseFloat(client.storage_fee_sum)).toLocaleString('da-DK') }}</td>
                    </tr>
                    </tbody>
                </table>
                <div class="mb-3" v-if="activeTab === 'tab1'">
                    <div>Total count: <b>{{ pagination.total }}</b></div>
                    <div>Total value: <b>{{ clientsWithoutInvoicesTotalSum.toLocaleString('da-DK') }}</b></div>
                </div>
                <div class="mt-3 d-flex justify-content-between align-items-center" v-if="activeTab === 'tab1'">
                    <div>
                        <label class="pr-2">Per page:</label>
                        <div class="d-inline-flex align-items-center bg-white border rounded-lg div-focus border-2">
                            <select class="form-control bg-white border-0 py-0 shadow-none" v-model="filters.per_page" style="width: 80px;">
                                <option v-for="option in perPageOptions" :key="option" :value="option">
                                    {{ option }}
                                </option>
                            </select>
                        </div>
                    </div>
                    <pagination
                        v-if="pagination.last_page > 1"
                        :current-page-prop="pagination.current_page"
                        :total-pages="pagination.last_page"
                        @change-page="changePage"
                    ></pagination>
                </div>
            </div>
            <div
                class="tab-pane fade"
                :class="{ 'show active': activeTab === 'tab2' }"
                id="tab2"
                role="tabpanel"
            >
                <div v-if="!filters.client || !filters.client.id" class="alert alert-info">
                    Please select client
                </div>
                <div v-else>
                    <table class="table table-bordered">
                        <thead>
                        <tr>
                            <th @click="sortDetailed('id')">Booking</th>
                            <th @click="sortDetailed('dropoff_end')">Date</th>
                            <th @click="sortDetailed('dropoff_end')">Week</th>
                            <th @click="sortDetailed('status_id')">Status</th>
                            <th @click="sortDetailed('total_price')">Total Price</th>
                            <th>Client Price</th>
                            <th>Customer Price</th>
                            <th>
                                <input type="checkbox"
                                       :key="`header-checkbox-${headerCheckboxStatus}`"
                                       :checked="headerCheckboxStatus === 'checked'"
                                       :indeterminate.prop="headerCheckboxStatus === 'indeterminate'"
                                       @click="toggleHeaderCheckbox">
                            </th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-if="selectedCount > 0 && !allSelectedMode">
                            <td colspan="8" class="text-center bg-light">
                                <b>{{ selectedCount }}</b> bookings selected.
                                <template v-if="showSelectAllMatchingBanner && isAllPageSelected && paginationDetailed.total > clientsWithoutInvoicesDetailed.length">
                                    <a href="#" @click.prevent="allSelectedMode = true">Select all <b>{{ paginationDetailed.total }}</b> bookings that match this filter</a>
                                </template>
                                <span v-else>&nbsp;</span>
                                <a href="#" @click.prevent="resetSelection()">Clear selection</a>
                            </td>
                        </tr>
                        <tr v-if="allSelectedMode">
                            <td colspan="8" class="text-center bg-light">
                                All <b>{{ paginationDetailed.total }}</b> bookings that match this filter are selected.
                                <span v-if="Object.keys(excludedBookings).length > 0"> (<b>{{ Object.keys(excludedBookings).length }}</b> excluded)</span>
                                <a href="#" @click.prevent="resetSelection()">Clear selection</a>
                            </td>
                        </tr>
                        <tr v-for="booking in clientsWithoutInvoicesDetailed">
                            <td>
                                <a :href="`/bookings/${booking.id}/edit`"> <b>#B-{{ booking.id }}</b></a>
                            </td>
                            <td>{{ formatDate(booking.dropoff_date) }} {{ booking.dropoff_end ? booking.dropoff_end : '00:00' }}</td>
                            <td>{{ getWeek(booking.dropoff_date) }}</td>
                            <td>{{ booking.status }}</td>
                            <td>{{ booking.total_price - parseFloat(booking.deadline_fee) + parseFloat(booking.storage_fee) }}</td>
                            <td>{{ booking.total_price - parseFloat(booking.deadline_fee) + parseFloat(booking.storage_fee) }}</td>
                            <td>{{ booking.customerPrice }}</td>
                            <td>
                                <input type="checkbox"
                                       :checked="allSelectedMode ? !excludedBookings[booking.id] : selectedBookings[booking.id]"
                                       @change="toggleBookingSelection(booking.id)">
                            </td>
                        </tr>
                        </tbody>
                    </table>
                    <div class="mb-3">
                        <div>Total count: <b>{{ paginationDetailed.total }}</b></div>
                        <div>Total value: <b>{{ clientsWithoutInvoicesDetailedTotalSum.toLocaleString('da-DK') }}</b></div>
                    </div>
                    <div class="mt-3 d-flex justify-content-between align-items-center">
                        <div>
                            <label class="pr-2">Per page:</label>
                            <div class="d-inline-flex align-items-center bg-white border rounded-lg div-focus border-2">
                                <select class="form-control bg-white border-0 py-0 shadow-none" v-model="filters.per_page" style="width: 80px;">
                                    <option v-for="option in perPageOptions" :key="option" :value="option">
                                        {{ option }}
                                    </option>
                                </select>
                            </div>
                        </div>
                        <pagination
                            v-if="paginationDetailed.last_page > 1"
                            :current-page-prop="paginationDetailed.current_page"
                            :total-pages="paginationDetailed.last_page"
                            @change-page="changePage"
                        ></pagination>
                    </div>
                </div>
            </div>
            <div
                class="tab-pane fade"
                :class="{ 'show active': activeTab === 'tab3' }"
                id="tab3"
                role="tabpanel"
            >
                <table class="table table-bordered">
                    <thead>
                    <tr>
                        <th @click="sortInvoices('id')">Transaction ID</th>
                        <th @click="sortInvoices('booking_id')">Booking</th>
                        <th @click="sortInvoices('date')">Date</th>
                        <th @click="sortInvoices('booker_id')">Client</th>
                        <th @click="sortInvoices('status')">Status</th>
                        <th @click="sortInvoices('amount')">Price</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="invoice in createdInvoices">
                        <td>{{ invoice.id }}</td>
                        <td>
                            <a :href="`/bookings/${invoice.booking_id}/edit`"> <b>#B-{{ invoice.booking_id }}</b></a>
                        </td>
                        <td>{{ invoice.date }}</td>
                        <td>{{ clientListFormatted[invoice.booker_id] }}</td>
                        <td>{{ transactionStatuses[invoice.status] }}</td>
                        <td>{{ invoice.amount }} {{ invoice.currency }}</td>
                    </tr>
                    </tbody>
                </table>
                <div class="mt-3 d-flex justify-content-between align-items-center" v-if="activeTab === 'tab3'">
                    <div>
                        <label class="pr-2">Per page:</label>
                        <div class="d-inline-flex align-items-center bg-white border rounded-lg div-focus border-2">
                            <select class="form-control bg-white border-0 py-0 shadow-none" v-model="filters.per_page" style="width: 80px;">
                                <option v-for="option in perPageOptions" :key="option" :value="option">
                                    {{ option }}
                                </option>
                            </select>
                        </div>
                    </div>
                    <pagination
                        v-if="pagination.last_page > 1"
                        :current-page-prop="pagination.current_page"
                        :total-pages="pagination.last_page"
                        @change-page="changePage"
                    ></pagination>
                </div>
            </div>
        </div>
        <loading :active.sync="isLoading"
                 :can-cancel="false"
                 :is-full-page="true">
        </loading>
    </div>
</template>
<script>

import dateTimeMixin from "../mixins/dateTimeMixin";
import _ from 'lodash';
import moment from 'moment';


import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';
import 'vue-loading-overlay/dist/vue-loading.css';
import Loading from "vue-loading-overlay";
import datePickerOptionsMixin from "../mixins/datePickerOptionsMixin";
import Pagination from "./reusable/Pagination.vue";


export default {
    name: "Invoices",
    mixins: [dateTimeMixin, datePickerOptionsMixin],
    components: {DatePicker, Pagination, Loading},
    props: {
        transactionStatuses: {type: Array, required: true}
    },
    data() {
        return {
            isLoading: false,
            activeTab: "tab1",
            clientList: [],
            clientListFormatted: {},
            clientsWithoutInvoices: [],
            clientsWithoutInvoicesDetailed: [],
            selectedBookings: {},
            clientsWithoutInvoicesTotalSum: 0,
            clientsWithoutInvoicesDetailedTotalSum: 0,
            allSelectedMode: false,
            showSelectAllMatchingBanner: false,
            excludedBookings: {},
            filters: {
                date_single_from: moment().startOf('month').format('YYYY-MM-DD'),
                date_single_to: moment().endOf('month').format('YYYY-MM-DD'),
                client: {},
                sortClientColumn: 'client_name',
                sortClientDir: 'asc',
                sortDetailedColumn: 'id',
                sortDetailedDir: 'asc',
                sortInvoiceColumn: 'id',
                sortInvoiceDir: 'asc',
                per_page: 15,
                page: 1,
                bookingIds: []
            },
            pagination: {
                current_page: 1,
                last_page: 1,
                total: 0,
            },
            paginationDetailed: {
                current_page: 1,
                last_page: 1,
                total: 0,
            },
            isSelectedAll: false,
            createdInvoices: [],
            perPageOptions: [15, 25, 50, 100],
            error: '',
        }
    },
    computed: {
        selectedCount() {
            if (this.allSelectedMode) {
                return this.paginationDetailed.total - Object.keys(this.excludedBookings).length;
            }
            return Object.keys(this.selectedBookings).length;
        },
        isAllPageSelected() {
            if (!Array.isArray(this.clientsWithoutInvoicesDetailed) || this.clientsWithoutInvoicesDetailed.length === 0) return false;
            return this.clientsWithoutInvoicesDetailed.every(booking => {
                if (this.allSelectedMode) {
                    return !this.excludedBookings[booking.id];
                }
                return this.selectedBookings[booking.id];
            });
        },
        isAnyPageSelected() {
            if (!Array.isArray(this.clientsWithoutInvoicesDetailed) || this.clientsWithoutInvoicesDetailed.length === 0) return false;
            return this.clientsWithoutInvoicesDetailed.some(booking => {
                if (this.allSelectedMode) {
                    return !this.excludedBookings[booking.id];
                }
                return this.selectedBookings[booking.id];
            });
        },
        headerCheckboxStatus() {
            if (this.allSelectedMode) {
                return Object.keys(this.excludedBookings).length === 0 ? 'checked' : 'indeterminate';
            }

            // Check if any booking is selected across ANY page (even if not on the current one)
            const hasSelectionsGlobal = Object.keys(this.selectedBookings).length > 0;

            if (this.isAllPageSelected && this.clientsWithoutInvoicesDetailed.length > 0) {
                // If there's only one page and it's fully selected, it's 'checked'
                if (this.paginationDetailed.last_page <= 1) {
                    return 'checked';
                }
                // Multiple pages exist, so current page being fully selected is 'indeterminate'
                return 'indeterminate';
            }

            if (this.isAnyPageSelected || hasSelectionsGlobal) {
                return 'indeterminate';
            }

            return 'unchecked';
        }
    },
    methods: {
        changePage(page) {
            this.filters.page = page;
            this.debouncedGetInvoices(false, false, true);
        },
        sortDetailed(key) {
            if (key) {
                this.filters.sortDetailedColumn = key;
                this.filters.sortDetailedDir = this.filters.sortDetailedDir === 'asc' ? 'desc' : 'asc';
            }
        },
        sortInvoices(key) {
            if (key) {
                this.filters.sortInvoiceColumn = key;
                this.filters.sortInvoiceDir = this.filters.sortInvoiceDir === 'asc' ? 'desc' : 'asc';
            }
        },
        sortClient(key) {
            if (key) {
                this.filters.sortClientColumn = key;
                this.filters.sortClientDir = this.filters.sortClientDir === 'asc' ? 'desc' : 'asc';
            }
        },
        changeTab(tabId) {
            this.filters.page = 1;
            this.activeTab = tabId;
        },
        fetchData() {
            axios.get('/client-list', {params: {noRelatedData: 1}})
                .then(({data}) => {
                    this.clientListFormatted = {};
                    Object.entries(data.data).forEach(entry => {
                        const [key, value] = entry;
                        this.clientList.push({
                            id: value.id,
                            name: value.name,
                        });
                        this.clientListFormatted[value.id] = value.name;
                    });
                });
            this.getClientsWithoutInvoices();
        },
        getClientsWithoutInvoices(preserveSelection = false, refreshBookingIds = false) {
            if (!preserveSelection) {
                this.resetSelection();
            }
            this.isLoading = true;
            let filters = JSON.parse(JSON.stringify(this.filters));
            filters['refreshBookingIds'] = refreshBookingIds;
            axios.post('/clients-without-invoices?page=' + this.filters.page, filters)
                .then(({data}) => {
                    this.clientsWithoutInvoices = data.data.paginator.data
                    this.clientsWithoutInvoicesTotalSum = data.data.total_sum;
                    this.pagination.current_page = data.data.paginator.current_page;
                    this.pagination.last_page = data.data.paginator.last_page;
                    this.pagination.total = data.data.paginator.total;
                    this.$set(this.filters, 'bookingIds', data.data.bookingIds);
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },
        getClientsWithoutInvoicesDetailed(preserveSelection = false, refreshBookingIds = false) {
            if (!preserveSelection) {
                this.resetSelection();
            }
            this.isLoading = true;
            let filters = JSON.parse(JSON.stringify(this.filters));
            filters['refreshBookingIds'] = refreshBookingIds;
            axios.post('/clients-without-invoices-detailed?page=' + this.filters.page, filters)
                .then(({data}) => {
                    this.clientsWithoutInvoicesDetailed = data.data.paginator.data
                    this.clientsWithoutInvoicesDetailedTotalSum = data.data.total_sum;
                    this.paginationDetailed.current_page = data.data.paginator.current_page;
                    this.paginationDetailed.last_page = data.data.paginator.last_page;
                    this.paginationDetailed.total = data.data.paginator.total;
                    this.$set(this.filters, 'bookingIds', data.data.bookingIds);
                    console.log('Detailed pagination:', this.paginationDetailed);
                }).catch((error) => {
                console.log(error)
            })
                .finally(() => {
                    this.isLoading = false;
                });
        },
        getCreatedInvoices(preserveSelection, refreshBookingIds = false) {
            if (!preserveSelection) {
                this.resetSelection();
            }
            this.isLoading = true;
            let filters = JSON.parse(JSON.stringify(this.filters));
            filters['refreshBookingIds'] = refreshBookingIds;
            axios.post('invoices/getInvoices?page=' + this.filters.page, filters)
                .then(({data}) => {
                    this.pagination.current_page = data.data.paginator.current_page;
                    this.pagination.last_page = data.data.paginator.last_page;
                    this.pagination.total = data.data.paginator.total;
                    this.createdInvoices = data.data.createdInvoices;
                    this.$set(this.filters, 'bookingIds', data.data.bookingIds);
                }).catch((error) => {
                    console.log(error)
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },
        setClientData(id, name) {
            this.resetSelection();
            this.filters.client = {
                id: id,
                name: name,
            }
            this.filters.page = 1;
            this.activeTab = 'tab2';
        },
        toggleHeaderCheckbox() {
            if (this.headerCheckboxStatus === 'checked' || this.headerCheckboxStatus === 'indeterminate') {
                this.resetSelection();
            } else {
                this.clientsWithoutInvoicesDetailed.forEach(booking => {
                    this.$set(this.selectedBookings, booking.id, true);
                });
                if (this.paginationDetailed.total > this.clientsWithoutInvoicesDetailed.length) {
                    this.showSelectAllMatchingBanner = true;
                }
            }
        },
        toggleBookingSelection(id) {
            if (this.allSelectedMode) {
                if (this.excludedBookings[id]) {
                    this.$delete(this.excludedBookings, id);
                } else {
                    this.$set(this.excludedBookings, id, true);
                }
            } else {
                if (this.selectedBookings[id]) {
                    this.$delete(this.selectedBookings, id);
                    this.showSelectAllMatchingBanner = false;
                } else {
                    this.$set(this.selectedBookings, id, true);
                }
            }
        },
        resetSelection() {
            this.selectedBookings = {};
            this.excludedBookings = {};
            this.allSelectedMode = false;
            this.isSelectedAll = false;
            this.showSelectAllMatchingBanner = false;
        },
        selectAll() {
            this.clientsWithoutInvoicesDetailed.forEach(booking => {
                this.$set(this.selectedBookings, booking.id, true);
            });
            this.isSelectedAll = true;
        },
        deSelectAll() {
            this.resetSelection();
        },
        createInvoices() {
            let payload = {};
            if (this.allSelectedMode) {
                payload = {
                    all_selected: true,
                    filters: this.filters,
                    excluded_ids: Object.keys(this.excludedBookings)
                };
            } else {
                let selectedIds = [];
                Object.entries(this.selectedBookings).forEach(([id, selected]) => {
                    if (selected) {
                        selectedIds.push(id);
                    }
                });
                payload = {
                    selected_ids: selectedIds
                };
            }
            this.isLoading = true;
            this.error = '';
            axios.post('/create-invoice', payload)
                .then(({data}) => {
                    this.flashMessage.success({
                        title: '',
                        message: 'Invoices successfully created',
                        time: 2000,
                        blockClass: 'flash-message'
                    });
                    setTimeout(() => {
                        // window.location.reload();
                    }, 2000);
                }).catch((error) => {
                    let message = 'Failed to create invoice';
                    if (error.response && error.response.data && error.response.data.message) {
                        message = error.response.data.message;
                    }
                    this.error = message;
                    setTimeout(() => {
                        this.error = '';
                    }, 5000);
                console.log(error);
            })
                .finally(() => {
                    this.isLoading = false;
                });
        },
        getWeek(day) {
            return this.calculateWeekNumber(day);
        },
        calculateWeekNumber(date) {
            return moment(date).isoWeek();
        },
        preventDefault(event) {
            event.preventDefault();
        },
        debouncedGetInvoices: _.debounce(function (refreshBookingIds = true, resetPage = true, preserveSelection = false) {
            console.log('Filters updated: ', this.filters);
            if (!preserveSelection) {
                this.resetSelection();
            }
            if (resetPage) {
                this.filters.page = 1;
            }
            if (this.activeTab === 'tab1') {
                this.getClientsWithoutInvoices(preserveSelection, refreshBookingIds);
            } else if (this.activeTab === 'tab2' && this.filters.client && this.filters.client.id) {
                this.getClientsWithoutInvoicesDetailed(preserveSelection, refreshBookingIds);
            } else if (this.activeTab === 'tab3') {
                this.getCreatedInvoices(preserveSelection, refreshBookingIds);
            }
        }, 500),
    },
    watch: {
        'filters.date_single_from': function() { this.debouncedGetInvoices(); },
        'filters.date_single_to': function() { this.debouncedGetInvoices(); },
        'filters.client': function() { this.debouncedGetInvoices(false); },
        'filters.sortClientColumn': function() { this.debouncedGetInvoices(false); },
        'filters.sortClientDir': function() { this.debouncedGetInvoices(false); },
        'filters.sortDetailedColumn': function() { this.debouncedGetInvoices(false); },
        'filters.sortDetailedDir': function() { this.debouncedGetInvoices(false); },
        'filters.sortInvoiceColumn': function() { this.debouncedGetInvoices(false); },
        'filters.sortInvoiceDir': function() { this.debouncedGetInvoices(false); },
        'filters.per_page': function() { this.debouncedGetInvoices(false); },
        activeTab: function() { this.debouncedGetInvoices(false); }
    },
    created() {
        return this.fetchData();
    },
}
</script>

<style scoped>
.nav-link {
    border: 1px solid black;
}

.form-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.text-right {
    text-align: right;
}

.form-container label {
    margin-right: 10px;
}

.form-container .datetime,
.form-container select {
    margin-right: 10px;
}

.form-container .save-button {
    margin-left: auto;
}
#client-name >>> .vs__dropdown-toggle {
    height: 41px;
    border-radius: 0.5rem;
    border: 2px solid #ced4da;
    background-color: white;
}
#client-name {
    min-width: 200px;
}
#client-name >>> .vs__selected-options {
    padding: 0 8px;
}
#client-name >>> .vs__actions {
    padding: 4px 6px 0 3px;
}
</style>
